import { isEqual, isValid, max, min } from 'date-fns';
import type { Theme } from '@mui/material/styles';

import routes from '../config/routes';
import {
  AppPermissions,
  Country,
  DateValue,
  GroupBy,
  Permission,
  ReportFilter,
  ConciliationsStateTimeLapse,
  MastersImport,
  concDetErrorType,
  concDetErrorValueToKey,
  ConcDetErrorType,
  ConcDetErrorCount,
  ConciliationDetail,
  ConciliationHeader,
  concHeaderStateType,
  concHeaderStateValueToKey,
  ConcHeaderStateType,
  ConcHeaderStateCount,
} from '../types';
import constants from './constants';

/**
 * Determina si algún permiso de `userPermissions` existe en `permissions`
 * @param userPermissions Permisos que tiene asignado el usuario
 * @param permissions Permisos que se desean comparar
 * @returns true si algún elemento de `userPermissions` existe en `permissions` o si `userPermissions`
 * tiene elementos pero `permissions` no. Retorna false en caso de que `userPermissions` no tenga
 * elementos o si ningún elemento de `userPermissions` existe en `permissions`.
 *
 */
export const hasPermissions = (
  userPermissions: string[],
  permissions: AppPermissions | AppPermissions[]
) => {
  if (!userPermissions.length) return false;
  if (Array.isArray(permissions))
    return !permissions.length || userPermissions.some(k => permissions.some(j => j === k));
  return userPermissions.includes(permissions);
};

export const getRoutePathDefault = (userPermissions: string[]) => {
  const { protectedRoutes } = routes;

  const route = Object.values(protectedRoutes).find(
    i => i.defaultRedirectForPermission && userPermissions.includes(i.defaultRedirectForPermission)
  );
  return route?.path;
};

export const nameToInitials = (fullName: string) => {
  const parts = fullName.trim().split(' ');
  return parts.length === 1
    ? `${parts[0].charAt(0)}`
    : `${parts[0].charAt(0)}${parts.at(-1)?.charAt(0)}`;
};

export const undefinedIfEmpty = (value: string | undefined) => (value === '' ? undefined : value);

export const getDateValue = (value: object | null | undefined): DateValue => ({
  raw: value,
  accept: value && isValid(value) ? (value as Date) : null,
});

export const isEqualNullableDate = (
  date1: Date | null | undefined,
  date2: Date | null | undefined
) => (date1 && date2 && isEqual(date1, date2)) || (!date1 && !date2);

export const generateRowId = <T>(data: T[]): T[] =>
  data.map((item, index) => ({ ...item, rowId: index }));

export const onlyUnique = <T>(data: T[]): T[] => [...new Set(data)];

const ERROR = 'ERROR';
const OK = 'OK';
const COMPLETED = 'COMPLETED';
const PENDING = 'PENDING';

type Mode = 'pill' | 'card';
export const getStatusColor = (status: string, theme: Theme, mode: Mode = 'card') => {
  if (status.includes(ERROR))
    return mode === 'pill' ? theme.custom.status.error.fg : theme.palette.error.main;
  if (status.includes(OK) || status.includes(COMPLETED))
    return mode === 'pill' ? theme.custom.status.ok.fg : theme.palette.success.main;
  if (status.includes(PENDING)) return theme.palette.warning.main;
  return theme.palette.text.primary;
};

export const getStatusBackground = (status: string, theme: Theme, mode: Mode = 'card') => {
  if (status.includes(ERROR))
    return mode === 'pill' ? theme.custom.status.error.bg : theme.palette.error.light;
  if (status.includes(OK) || status.includes(COMPLETED))
    return mode === 'pill' ? theme.custom.status.ok.bg : theme.palette.success.light;
  if (status.includes(PENDING)) return theme.palette.warning.light;
  return theme.palette.background.paper;
};

export const getStatusConsDetail = (status: string, theme: Theme) => {
  const match = (Object.entries(concDetErrorType) as [ConcDetErrorType, string][]).find(
    ([, value]) => value === status
  );
  return match ? theme.custom.concDetErrorType[match[0]] : theme.palette.background.paper;
};

export const handleErrorPercent = (itemMode: 'error' | 'ok', percent: number) => {
  if (itemMode === 'ok') {
    if (percent === 100) {
      return '#60bb44';
    }
    if (percent > 90 && percent < 100) {
      return '#f8d330';
    }
    return '#ca2222';
  }
  if (percent === 0) {
    return '#60bb44';
  }
  if (percent > 0 && percent < 10) {
    return '#f8d330';
  }
  return '#ca2222';
};

export const getPercentColor = (theme: Theme, percent: number): string => {
  if (percent < 30) {
    return theme.palette.error.main;
  }
  if (percent < 80) {
    return theme.palette.warning.main;
  }
  return theme.palette.success.main;
};

export const groupBy = <TValue>(
  options: readonly TValue[],
  propGetter: (group: TValue) => string
): GroupBy<TValue> =>
  options.reduce((prev, curr) => {
    const group = propGetter(curr);
    if (!(group in prev)) prev[group] = [];
    prev[group].push(curr);
    return prev;
  }, {} as GroupBy<TValue>);

export const getCountriesForPermissions = (
  requiredPermissions: AppPermissions[],
  userPermissions: Permission[],
  countries: Country[] | undefined
) => {
  const userPermissionForResource = userPermissions.find(i =>
    requiredPermissions.includes(i.permission as AppPermissions)
  );
  if (userPermissionForResource) {
    const { countries: permissionCountries } = userPermissionForResource;
    if (permissionCountries.find(i => i.countryAcronym === 'FULL')) return countries || [];
    return (
      countries && countries.length > 0
        ? permissionCountries.map(p => countries?.find(c => c.countryCode === p.countryAcronym))
        : []
    ) as Country[];
  }

  return [] as Country[];
};

// Si la fecha endDate es válida y existe una startDate valida (accept), se asigna en startDate
// la más antigua entre ellas. Esto es para que nunca endDate sea más antigua que startDate.
export const getValidStartDate = (startDate: DateValue, endDate: Date | undefined | null) =>
  startDate.accept && endDate && isValid(endDate)
    ? min([endDate, startDate.accept])
    : startDate.accept;

// Si la nueva fecha startDate es válida y existe una endDate valida (accept), se asigna en endDate
// la más reciente  entre ellas. Esto es para que nunca startDate sea más reciente que endDate.
export const getValidEndDate = (startDate: Date | undefined | null, endDate: DateValue) =>
  endDate.accept && startDate && isValid(startDate)
    ? max([startDate, endDate.accept])
    : endDate.accept;

export const getSelectedStartDate = (filters: ReportFilter) =>
  filters.startAccountingDate.accept ||
  filters.startBusinessDate.accept ||
  filters.startOperationDate.accept;

export const getSelectedEndDate = (filters: ReportFilter) =>
  filters.endAccountingDate.accept ||
  filters.endBusinessDate.accept ||
  filters.endOperationDate.accept;

// eslint-disable-next-line @typescript-eslint/ban-types
export const isFunction = (value: unknown): value is Function => value instanceof Function;

export const masterSort = (a: MastersImport, b: MastersImport): number => {
  if (a.state < b.state) return -1;
  if (a.state > b.state) return 1;

  if (a.importDate && b.importDate) {
    if (a.importDate > b.importDate) return -1;
    if (a.importDate < b.importDate) return 1;
  }

  if (a.type < b.type) return -1;
  if (a.type > b.type) return 1;

  if (a.country < b.country) return -1;
  if (a.country > b.country) return 1;

  return 0;
};

export const handleConciliationsStateViewMode = (viewMode: ConciliationsStateTimeLapse) => {
  const dates = constants.dashboardIncorporationDates;
  let from = dates.yesterday;
  let to = dates.yesterday;
  if (viewMode === ConciliationsStateTimeLapse.currentMonth) {
    from = dates.currentMonthFrom;
    to = dates.yesterday;
  } else if (viewMode === ConciliationsStateTimeLapse.lastMonth) {
    from = dates.lastMonthFrom;
    to = dates.lastMonthTo;
  }
  return { from, to };
};

export const capitalize = (str: string, lower = false) =>
  (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, match => match.toUpperCase());
export const isIncompleteDateRange = (startDate: DateValue, endDate: DateValue) =>
  Boolean(startDate.accept) !== Boolean(endDate.accept);

export const noValidDateRanges = (filters: ReportFilter) =>
  !(filters.startBusinessDate.accept && filters.endBusinessDate.accept) &&
  !(filters.startOperationDate.accept && filters.endOperationDate.accept) &&
  !(filters.startAccountingDate.accept && filters.endAccountingDate.accept);

export const hasIncompleteRanges = (filters: ReportFilter) =>
  isIncompleteDateRange(filters.startBusinessDate, filters.endBusinessDate) ||
  isIncompleteDateRange(filters.startOperationDate, filters.endOperationDate) ||
  isIncompleteDateRange(filters.startAccountingDate, filters.endAccountingDate);

export const getPercent = (part: number, total: number) => {
  if (total === 0) return 0;
  const result = (part / total) * 100;
  return Math.round((result + Number.EPSILON) * 100) / 100;
};

const keys = Object.keys(concDetErrorType) as Array<keyof typeof concDetErrorType>;

const INITIAL_COUNTS: ConcDetErrorCount = keys.reduce((acc, key) => {
  acc[key] = 0;
  return acc;
}, {} as ConcDetErrorCount);

export function countConciliationDetailErrors(details: ConciliationDetail[]): ConcDetErrorCount {
  const counts = { ...INITIAL_COUNTS };

  details.forEach(d => {
    const k = concDetErrorValueToKey[d.error as ConcDetErrorType];
    if (k) counts[k] += 1;
  });

  return counts;
}

const HeaderStateKeys = Object.keys(concHeaderStateType) as Array<keyof typeof concHeaderStateType>;

const HEADER_STATE_INITIAL_COUNTS: ConcHeaderStateCount = HeaderStateKeys.reduce((acc, key) => {
  acc[key] = 0;
  return acc;
}, {} as ConcHeaderStateCount);

export function countConciliationHeaderStates(details: ConciliationHeader[]): ConcHeaderStateCount {
  const counts = { ...HEADER_STATE_INITIAL_COUNTS };

  details.forEach(d => {
    const k = concHeaderStateValueToKey[d.status as ConcHeaderStateType];
    if (k) counts[k] += 1;
  });

  return counts;
}
